import { useCallback, useEffect, useRef, useState } from "react";
import type { CareMeetingRequirementView } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchCareMeetingRequirement,
  requireCareMeeting,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "./SupportView";

// TES-1602 (TES-1600 S2) — "Kräv vårdmöte" on the patient overview, for a
// patient whose tenant has made the onboarding meeting optional
// (features.careMeetingOptional). The server decides whether it applies:
// with the flag off, or for any other patient, the view says neither
// `canRequire` nor `required` and this renders nothing, so the overview is
// unchanged.
//
// Doctor only, outside the admin's read-only view, like "Begär
// komplettering". The staff-facing strings are proposed copy.

function feeText(feeMinor: number): string {
  return feeMinor === 0
    ? "är kostnadsfritt för patienten"
    : `kostar ${Math.round(feeMinor / 100)} kr`;
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Stockholm",
  });
}

export function CareMeetingRequirementPanel({
  patientId,
  onUnauthorized,
  onRequired,
}: {
  patientId: string;
  onUnauthorized: () => void;
  onRequired?: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const [view, setView] = useState<CareMeetingRequirementView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  const isDoctor = claims?.role === "doctor" && !readOnly;

  const load = useCallback(async () => {
    try {
      setView(await fetchCareMeetingRequirement(patientId));
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
      // Otherwise nothing renders: the action is offered only when the
      // server has said it applies.
    }
  }, [patientId]);

  useEffect(() => {
    if (isDoctor) void load();
  }, [isDoctor, load]);

  if (!isDoctor || !view) return null;
  if (!view.canRequire && !view.required) return null;

  async function submit() {
    if (
      !window.confirm(
        "Kräv vårdmöte? Patienten får ett kort i appen och en pushnotis om att boka ett vårdmöte.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setView(await requireCareMeeting(patientId));
      onRequired?.();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError("Vårdmötet kunde inte krävas. Försök igen.");
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md">
      <h2 className="font-display text-lg mb-xs">Vårdmöte</h2>
      {view.required ? (
        <p className="text-sm" role="status">
          {view.required.reason === "declaration_flags" ? (
            // TES-1643 (Ö-1008) — set automatically, by no doctor: the text
            // is the ticket's, the date and price follow.
            <>
              Vårdmöte krävs – flaggor i hälsodeklarationen (
              {formatDay(view.required.at)}). Mötet {feeText(view.feeMinor)}.
            </>
          ) : (
            <>
              Vårdmöte krävt {formatDay(view.required.at)}
              {view.required.byName ? ` av ${view.required.byName}` : ""}.
            </>
          )}{" "}
          {view.meetingBooked
            ? "Patienten har bokat mötet."
            : "Patienten har inte bokat mötet ännu."}
        </p>
      ) : (
        <div className="flex flex-col gap-xs text-sm">
          <p className="text-textSecondary">
            Vårdmötet är frivilligt för patienten. Läkaren bedömer
            hälsodeklarationen och provsvaren skriftligt. Kräver du ett möte
            ombeds patienten boka det i appen. Mötet räknas som bedömningsmötet
            och {feeText(view.feeMinor)}.
          </p>
          <div>
            <button
              type="button"
              className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
              disabled={busy}
              onClick={() => void submit()}
            >
              Kräv vårdmöte
            </button>
          </div>
          {error ? (
            <p className="text-xs text-danger" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
